import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectBasketId, selectStrategyBuilderError, selectStrategyBuilderMode, selectTargetValues, selectTradeOrders } from '../../selectors/strategyBuilder'
import { selectPositions } from '../../selectors/positions'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { getSecuritySnapshot } from '../../selectors/security'
import store from '../../store'
import { getSecurityKey } from '../../util/security'
import { getPayoffLegs, getPayoffProfitData, getSortedTradedExpiries } from '../../util/strategyBuilder'
import { PayoffStrikeData, setAddOrderEnabled, setPayoffGraphData, TradeOrder, updateStrategyInfo, updateTargetValues } from '../../reducer/strategyBuilder.reducer'
import { SdData } from '../payoffContainer/livePayoffContainer'
import { selectUnderlyingFuturesTradeData } from '../../selectors/futures'
import { selectOrdersHistory } from '../../selectors/orders'
import { Order, OrderStatus } from '../../proto/generated/order'
import { debounce } from 'throttle-debounce'
import { Position } from '../../proto/generated/position'
import { Security } from '../../proto/generated/security'
import { getStrategyInfo } from '../../util/tradepanel'
import { GetOptionChainRequestV2 } from '../../proto/generated/candle-data-depot-option-chain'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { fetchOptionChainV2 } from '../../util/options'
import { strikeValues } from '../../util/tradePage'

/** Payoff / trade-order side effects isolated so option chain tab children do not re-render on add/remove. */
const LiveStrategyBuilderPayoffEffects = () => {
    const dispatch = useDispatch()
    const { primaryOrUnderlying: underlying, primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()

    const strategyBuilderMode = useSelector(selectStrategyBuilderMode)
    const orderHistory = useSelector(selectOrdersHistory)
    const orderHistoryMap = useMemo(() => (orderHistory || []).reduce((acc, o) => ({ ...acc, [o.orderId]: o }), {} as Record<string, Order>), [orderHistory])
    const tradeOrders = useSelector(selectTradeOrders)
    const positions = useSelector(selectPositions)
    const basketId = useSelector(selectBasketId)
    const optionsTradeData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const futuresData = useSelector(selectUnderlyingFuturesTradeData(underlyingToken))
    const basketPositions = useMemo(() => positions.filter((p) => p.basketId === basketId), [positions, basketId])
    const [tradedExpiries, setTradedExpiries] = useState<number[]>([])
    const tradeOrdersRef = useRef(tradeOrders)
    const basketPositionsRef = useRef(basketPositions)
    tradeOrdersRef.current = tradeOrders
    basketPositionsRef.current = basketPositions
    useEffect(() => {
        getSortedTradedExpiries(tradeOrdersRef.current, basketPositionsRef.current).then(setTradedExpiries)
    }, [tradeOrders?.length ?? 0, basketPositions?.length ?? 0, basketId])
    const nearestExpiry = tradedExpiries?.length ? tradedExpiries[0] : null
    const optionsTradeDataRef = useRef(optionsTradeData || [])
    const futuresTradeDataRef = useRef(futuresData || [])
    const builderError = useSelector(selectStrategyBuilderError)

    const { targetDate, ivMultiplier, spotBasePrice, spotTargetPrice } = useSelector(selectTargetValues)

    const elligibleTradeOrders = useMemo(() => {
        if (strategyBuilderMode === 'trade') return tradeOrders.filter((o) => o.selected)

        return tradeOrders.filter((o) => {
            return (!o.referenceOrderId && o.selected) || (o.referenceOrderId && orderHistoryMap[o.referenceOrderId]?.status !== OrderStatus.EXECUTED)
        })
    }, [tradeOrders, strategyBuilderMode, orderHistoryMap])

    useEffect(() => {
        if (!optionsTradeData) return
        optionsTradeDataRef.current = optionsTradeData
    }, [optionsTradeData])

    useEffect(() => {
        if (!futuresData) return
        futuresTradeDataRef.current = futuresData
    }, [futuresData])

    useEffect(() => {
        const inProgressOrderStatus = [OrderStatus.INACTIVE, OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING]
        const isAnyOrderInProgress = tradeOrders.some((order) => inProgressOrderStatus.includes(orderHistoryMap[order.referenceOrderId]?.status))

        dispatch(setAddOrderEnabled(!isAnyOrderInProgress))
    }, [tradeOrders, orderHistoryMap, strategyBuilderMode, dispatch])

    const hasInitializedPriceRef = useRef(false)
    const prevUnderlyingTokenRef = useRef(underlyingToken)
    const isTokenValid = underlyingToken && !underlyingToken.startsWith('EXCHANGE_UNKNOWN') && underlyingToken.includes(':')

    useEffect(() => {
        if (!isTokenValid) {
            if (prevUnderlyingTokenRef.current && !prevUnderlyingTokenRef.current.startsWith('EXCHANGE_UNKNOWN')) {
                hasInitializedPriceRef.current = false
            }
            return
        }

        if (prevUnderlyingTokenRef.current !== underlyingToken) {
            prevUnderlyingTokenRef.current = underlyingToken
            hasInitializedPriceRef.current = false
        }

        if (!spotTargetPrice && !spotBasePrice) {
            hasInitializedPriceRef.current = false
        }

        if (hasInitializedPriceRef.current) return
        if (spotTargetPrice && spotBasePrice) return

        const tryInitFromSnapshot = () => {
            if (hasInitializedPriceRef.current) return

            const { spotTargetPrice: currentSpotTarget, spotBasePrice: currentSpotBase } = selectTargetValues(store.getState())
            if (currentSpotTarget && currentSpotBase) return

            const snapshotLtp = getSecuritySnapshot(underlyingToken)?.lastTradedPrice
            const hasValidLtp = typeof snapshotLtp === 'number' && snapshotLtp > 0

            if (hasValidLtp) {
                hasInitializedPriceRef.current = true
                dispatch(
                    updateTargetValues({
                        spotBasePrice: snapshotLtp,
                        spotTargetPrice: snapshotLtp,
                    })
                )
            }
        }

        tryInitFromSnapshot()
        if (hasInitializedPriceRef.current) return

        return store.subscribe(tryInitFromSnapshot)
    }, [underlyingToken, isTokenValid, spotTargetPrice, spotBasePrice, dispatch])

    const retryCounter = useRef(0)
    const fetchingOptionChainData = useRef(false)

    const calculatePayoffGraphData = useCallback(
        debounce(
            50,
            async (
                underlyingSec: Security,
                elligibleTradeOrdersArg: TradeOrder[],
                basketPositionsArg: Position[],
                targetDateArg: number,
                ivMultiplierArg: number,
                spotBasePriceArg: number,
                spotTargetPriceArg: number,
                nearestExpiryArg: number
            ) => {
                const optionsTradesData = optionsTradeDataRef.current.find((t) => t.expiration.expiry.expiry === nearestExpiryArg)
                if ((!optionsTradesData || optionsTradesData?.trades?.length <= strikeValues.maxValue) && retryCounter.current < 40) {
                    if (!fetchingOptionChainData.current) {
                        const expiries = (await getSortedTradedExpiries(elligibleTradeOrdersArg, basketPositionsArg || [])) || []
                        fetchingOptionChainData.current = true

                        if (expiries.length || optionsTradesData?.trades?.length <= strikeValues.maxValue) {
                            const payload: GetOptionChainRequestV2 = {
                                security: { code: underlyingSec.code, exchange: underlyingSec.exchange },
                                expiries,
                                noOfStrikes: -1,
                                requestedFields: [],
                                timeBucket: {
                                    timeSeries: TimeBucket_TimeSeries.DAY,
                                    value: 1,
                                },
                                sendFutures: true,
                            }
                            await fetchOptionChainV2(payload, dispatch, expiries.length ? optionsTradeData : null, 'both', false)
                        }
                        fetchingOptionChainData.current = false
                    }
                    retryCounter.current += 1
                    calculatePayoffGraphData(
                        underlyingSec,
                        elligibleTradeOrdersArg,
                        basketPositionsArg,
                        targetDateArg,
                        ivMultiplierArg,
                        spotBasePriceArg,
                        spotTargetPriceArg,
                        nearestExpiryArg
                    )
                    return
                }

                if (!optionsTradesData) {
                    return
                }

                const newSdData: SdData[] = []
                const atmStrike = optionsTradesData.atmStrike
                const loop = [-2, -1, 1, 2]

                if (atmStrike) {
                    const sdPercent = optionsTradesData.standardDeviation
                    const zeroStrike = atmStrike / (underlyingSec.priceMultiplier ?? 100)
                    loop.forEach((num) => {
                        newSdData.push({
                            label: `${num} SD`,
                            price: Number((zeroStrike * (1 + 0.01 * sdPercent * num)).toFixed(0)),
                        })
                    })
                }
                const { profitData, targetPnl } = await getPayoffProfitData(
                    elligibleTradeOrdersArg,
                    basketPositionsArg,
                    targetDateArg,
                    ivMultiplierArg,
                    spotBasePriceArg,
                    spotTargetPriceArg,
                    optionsTradeDataRef.current,
                    futuresTradeDataRef.current,
                    underlyingSec
                )

                const payoffStrikes: PayoffStrikeData[] = optionsTradesData.trades?.map((t) => ({
                    strike: t.strikeCol.strike / (underlyingSec.priceMultiplier ?? 100),
                    callDelta: t.callOption?.optionGreeks?.delta ?? 0,
                    callOi: t.callOption?.openInterest ?? 0,
                    putDelta: t.putOption?.optionGreeks?.delta ?? 0,
                    putOi: t.putOption?.openInterest ?? 0,
                }))

                const payoffLegs = await getPayoffLegs(elligibleTradeOrdersArg, basketPositionsArg)

                dispatch(
                    setPayoffGraphData({
                        payoffProfitData: profitData,
                        payoffStrikes,
                        sdData: newSdData,
                        optionsTradesData,
                        payoffLegs,
                        targetPnl,
                    })
                )
            }
        ),
        []
    )

    useEffect(() => {
        if (
            (strategyBuilderMode === 'trade' && !elligibleTradeOrders?.length) ||
            (strategyBuilderMode !== 'trade' && !basketPositions?.length) ||
            !!builderError ||
            !optionsTradeData ||
            !tradedExpiries.length
        ) {
            return
        }

        const nearestExpiryVal = tradedExpiries[0]
        const optionsData = optionsTradeData.find((v) => v.expiration.expiry.expiry === nearestExpiryVal)
        if (!optionsData) {
            return
        }

        getStrategyInfo(elligibleTradeOrders, strategyBuilderMode === 'trade' ? [] : basketPositions, optionsTradeData).then((strategyInfo) => {
            if (strategyInfo) dispatch(updateStrategyInfo(strategyInfo))
        })
    }, [elligibleTradeOrders, basketPositions, strategyBuilderMode, builderError, optionsTradeData, tradedExpiries, dispatch])

    const payoffRecalcKey = useMemo(() => {
        const orderKey = elligibleTradeOrders
            .map((o) => `${o.clientOrderId}:${o.price}:${o.quantity}:${o.transactionType}:${o.selected}:${getSecurityKey(o.security.exchange, o.security.code)}`)
            .join('|')
        const positionKey = basketPositions
            .map((p) => `${getSecurityKey(p.security.exchange, p.security.code)}:${p.closePosition}:${p.closePositionValue}`)
            .join('|')
        return [orderKey, positionKey, targetDate, ivMultiplier, spotBasePrice, spotTargetPrice, nearestExpiry].join('#')
    }, [elligibleTradeOrders, basketPositions, targetDate, ivMultiplier, spotBasePrice, spotTargetPrice, nearestExpiry])

    useEffect(() => {
        retryCounter.current = 0
        if (!underlying || nearestExpiry == null) return
        calculatePayoffGraphData(
            underlying,
            elligibleTradeOrders,
            basketPositions,
            targetDate,
            ivMultiplier,
            spotBasePrice,
            spotTargetPrice,
            nearestExpiry
        )
    }, [payoffRecalcKey, underlying, nearestExpiry, calculatePayoffGraphData])

    return null
}

export default memo(LiveStrategyBuilderPayoffEffects)
